/**
 * Learner activity table component for instructor dashboard.
 */

import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { UserActivitySummary, UserSummary } from '../../services/instructor';

type SortField =
  | 'display_name'
  | 'cohort'
  | 'assigned_instructor'
  | 'active_runs'
  | 'completed_runs'
  | 'average_grade'
  | 'last_activity';

interface UserActivityTableProps {
  users: UserActivitySummary[];
  onUserSelect: (userId: number) => void;
  loading?: boolean;
  title?: string;
  instructors?: UserSummary[];
  canManageInstructors?: boolean;
  onInstructorAssign?: (learnerId: number, instructorId: number | null) => void;
}

function learnerName(user: UserActivitySummary): string {
  return user.display_name || user.username;
}

function instructorName(user: UserActivitySummary): string {
  return user.assigned_instructor?.display_name || user.assigned_instructor?.username || 'Unassigned';
}

export function UserActivityTable({
  users,
  onUserSelect,
  loading = false,
  title,
  instructors = [],
  canManageInstructors = false,
  onInstructorAssign,
}: UserActivityTableProps) {
  const [sortField, setSortField] = useState<SortField>('display_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const heading = title ?? 'Learners';

  const sortedUsers = [...users].sort((a, b) => {
    const valueFor = (user: UserActivitySummary): string | number => {
      if (sortField === 'display_name') return learnerName(user).toLowerCase();
      if (sortField === 'cohort') return (user.cohort || '').toLowerCase();
      if (sortField === 'assigned_instructor') return instructorName(user).toLowerCase();
      if (sortField === 'last_activity') return user.last_activity ? new Date(user.last_activity).getTime() : 0;
      return user[sortField] ?? 0;
    };

    const aValue = valueFor(a);
    const bValue = valueFor(b);
    const comparison =
      typeof aValue === 'number' && typeof bValue === 'number'
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue));

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'display_name' ? 'asc' : 'desc');
    }
  };

  const sortLabel = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' asc' : ' desc';
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getGradeBadgeColor = (grade: number | undefined): 'success' | 'default' | 'primary' | 'warning' | 'danger' | 'info' => {
    if (grade === undefined || grade === null) return 'default';
    if (grade >= 90) return 'success';
    if (grade >= 80) return 'info';
    if (grade >= 70) return 'warning';
    if (grade >= 60) return 'warning';
    return 'danger';
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="p-6 border-b border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-white">{heading}</h2>
            <p className="text-sm text-gray-400 mt-1">
              {sortedUsers.length} learner{sortedUsers.length === 1 ? '' : 's'} in the current view
            </p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('display_name')}
                  className="text-xs font-medium text-gray-400 uppercase hover:text-white"
                >
                  Name{sortLabel('display_name')}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('cohort')}
                  className="text-xs font-medium text-gray-400 uppercase hover:text-white"
                >
                  Cohort{sortLabel('cohort')}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('assigned_instructor')}
                  className="text-xs font-medium text-gray-400 uppercase hover:text-white"
                >
                  Instructor{sortLabel('assigned_instructor')}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('active_runs')}
                  className="text-xs font-medium text-gray-400 uppercase hover:text-white"
                >
                  Active{sortLabel('active_runs')}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('completed_runs')}
                  className="text-xs font-medium text-gray-400 uppercase hover:text-white"
                >
                  Completed{sortLabel('completed_runs')}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('average_grade')}
                  className="text-xs font-medium text-gray-400 uppercase hover:text-white"
                >
                  Avg Grade{sortLabel('average_grade')}
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('last_activity')}
                  className="text-xs font-medium text-gray-400 uppercase hover:text-white"
                >
                  Last Activity{sortLabel('last_activity')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {sortedUsers.map((user) => (
              <tr
                key={user.user_id}
                className="hover:bg-gray-800/50 transition-colors"
                data-testid={`user-row-${user.user_id}`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-white">{learnerName(user)}</div>
                  <div className="text-sm text-gray-400">{user.username} | {user.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                  {user.cohort || 'No cohort'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {canManageInstructors && onInstructorAssign ? (
                    <select
                      value={user.assigned_instructor_id ?? ''}
                      onChange={(event) => {
                        const value = event.target.value;
                        onInstructorAssign(user.user_id, value ? Number(value) : null);
                      }}
                      className="min-w-[180px] bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white"
                      data-testid={`assigned-instructor-${user.user_id}`}
                    >
                      <option value="">Unassigned</option>
                      {instructors.map((instructor) => (
                        <option key={instructor.id} value={instructor.id}>
                          {instructor.display_name || instructor.username}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-sm text-gray-300">{instructorName(user)}</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.active_runs > 0 ? (
                    <Badge variant="success">{user.active_runs}</Badge>
                  ) : (
                    <span className="text-sm text-gray-400">0</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                  {user.completed_runs}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.average_grade !== undefined && user.average_grade !== null ? (
                    <Badge variant={getGradeBadgeColor(user.average_grade)}>
                      {user.average_grade.toFixed(1)}%
                    </Badge>
                  ) : (
                    <span className="text-sm text-gray-400">N/A</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                  {formatDate(user.last_activity)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onUserSelect(user.user_id)}
                    data-testid={`view-user-${user.user_id}`}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {sortedUsers.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            No learners match the current filters.
          </div>
        )}
      </div>
    </Card>
  );
}
